// ─── MobileHeader ────────────────────────────────────────────────────────────
//
// Mobile web navigation replacement for the retired bottom tab bar.
// Sticky top header with brand + hamburger button. Tapping the hamburger
// opens a right-slide drawer containing the full navigation set, profile
// actions, and sign out.
//
// Visible only at < md (< 768px). At md+ the existing <TopNav> takes over.
// The mobile React Native app has its own navigation and is not touched
// here — this component is web-only.
//
// Accessibility:
//   - Hamburger button: aria-label, aria-expanded, aria-controls
//   - Drawer: role=dialog, aria-modal=true, aria-labelledby
//   - Focus management: first focusable element receives focus on open;
//     focus is restored to the hamburger button on close
//   - Escape key closes the drawer
//   - Backdrop click closes the drawer
//   - Body scroll is locked while the drawer is open
//   - prefers-reduced-motion: slide transition is swapped for instant appear
//   - All nav items preserve touch targets >= 44px
// ──────────────────────────────────────────────────────────────────────────────

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { supabase, getCurrentUser } from '../../lib/supabase';
import { IconBell } from '../Icons';
import { useCheckInDue } from '../OnboardingChecklist';

interface Props {
  isAdmin?: boolean;
  isClinician?: boolean;
}

// Primary + secondary items in a single source of truth so the drawer,
// TopNav, and test fixtures stay in sync.
const PRIMARY_NAV = [
  { to: '/dashboard', label: 'Overview' },
  { to: '/sleep', label: 'Sleep' },
  { to: '/movement', label: 'Movement' },
  { to: '/nutrition', label: 'Nutrition' },
  { to: '/stress', label: 'Stress' },
] as const;

const SECONDARY_NAV = [
  { to: '/social-substances', label: 'Social & Substances' },
  { to: '/phenotype', label: 'Drivers & Patterns' },
  { to: '/checkin', label: 'Check-In' },
] as const;

const PROFILE_NAV = [
  { to: '/action-plan', label: 'Action Plan' },
  { to: '/labs', label: 'All Labs' },
  { to: '/connect', label: 'Data Sources' },
  { to: '/history', label: 'History' },
  { to: '/settings', label: 'Settings' },
] as const;

// Menu section component for readability
function MenuSection({
  label,
  items,
  onNavigate,
}: {
  label?: string;
  items: readonly { to: string; label: string }[];
  onNavigate: () => void;
}) {
  return (
    <div className="py-2">
      {label && (
        <p className="px-4 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-arete-muted">
          {label}
        </p>
      )}
      {items.map(({ to, label: itemLabel }) => (
        <NavLink
          key={to}
          to={to}
          onClick={onNavigate}
          className="flex items-center px-4 min-h-[44px] text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-arete-blue/40"
          style={({ isActive }) => ({
            color: isActive ? 'var(--color-arete-navy, #0B1F3A)' : 'rgba(27,48,34,0.72)',
            fontWeight: isActive ? 600 : 400,
            background: isActive ? 'rgba(111,138,90,0.10)' : 'transparent',
          })}
        >
          {itemLabel}
        </NavLink>
      ))}
    </div>
  );
}

export default function MobileHeader({ isAdmin, isClinician }: Props) {
  const [open, setOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const checkInDue = useCheckInDue();
  const location = useLocation();
  const navigate = useNavigate();

  const hamburgerRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  const firstFocusableRef = useRef<HTMLAnchorElement | null>(null);

  // Close on route change
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Escape key + body scroll lock while drawer is open
  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', handleKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  // Return focus to the hamburger button when drawer closes
  useEffect(() => {
    if (!open && hamburgerRef.current && document.activeElement !== hamburgerRef.current) {
      // Only restore focus if the drawer was previously open — skip first mount
      const wasInsideDrawer = drawerRef.current?.contains(document.activeElement);
      if (wasInsideDrawer) hamburgerRef.current.focus();
    }
  }, [open]);

  // Fetch unread alert count for clinicians (same behavior as TopNav)
  useEffect(() => {
    if (!isClinician && !isAdmin) return;
    let cancelled = false;
    async function fetchAlertCount() {
      const user = await getCurrentUser();
      if (!user || cancelled) return;
      const { count } = await supabase
        .from('alerts')
        .select('id', { count: 'exact', head: true })
        .eq('clinician_id', user.id)
        .eq('read', false);
      if (!cancelled) setUnreadCount(count || 0);
    }
    fetchAlertCount();
    const interval = setInterval(fetchAlertCount, 60000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [isClinician, isAdmin]);

  const handleNavigate = useCallback(() => {
    setOpen(false);
  }, []);

  const handleSignOut = useCallback(async () => {
    setOpen(false);
    await supabase.auth.signOut();
    navigate('/login');
  }, [navigate]);

  return (
    <>
      {/* ── Sticky top header (mobile web only) ───────────────────────────── */}
      <header
        className="md:hidden sticky top-0 z-30 flex items-center justify-between px-4 py-2"
        style={{
          background: 'rgba(243,247,240,0.97)',
          backdropFilter: 'blur(20px) saturate(1.1)',
          WebkitBackdropFilter: 'blur(20px) saturate(1.1)',
          borderBottom: '1px solid rgba(214,224,207,0.45)',
        }}
      >
        <Link
          to="/dashboard"
          className="py-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-arete-blue/40 rounded"
          style={{
            fontFamily: "'Cormorant Garamond', 'DM Serif Display', Georgia, serif",
            fontSize: '1rem',
            fontWeight: 600,
            color: '#1B3022',
            letterSpacing: '0.02em',
          }}
        >
          Arete Integrated Health
        </Link>

        <div className="flex items-center gap-1">
          {(isClinician || isAdmin) && unreadCount > 0 && (
            <Link
              to="/patients"
              className="relative p-2 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-arete-blue/40"
              aria-label={`${unreadCount} unread alert${unreadCount !== 1 ? 's' : ''}`}
            >
              <IconBell size={18} color="#EF4444" />
              <span className="absolute top-0.5 right-0.5 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-1">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            </Link>
          )}

          <button
            ref={hamburgerRef}
            type="button"
            onClick={() => setOpen(true)}
            aria-label="Open navigation menu"
            aria-expanded={open}
            aria-controls="mobile-nav-drawer"
            className="p-2.5 min-w-[44px] min-h-[44px] rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-arete-blue/40"
            style={{ color: '#1B3022' }}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
        </div>
      </header>

      {/* ── Drawer + backdrop (mobile web only) ──────────────────────────── */}
      {open && (
        <div
          className="md:hidden fixed inset-0 z-50"
          onClick={() => setOpen(false)}
          // motion-reduce utility swaps the slide transition for no-transition
          aria-hidden="false"
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40 motion-reduce:transition-none transition-opacity"
            style={{ animation: 'fadeIn 0.18s ease-out' }}
          />

          {/* Drawer */}
          <aside
            id="mobile-nav-drawer"
            ref={drawerRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="mobile-nav-drawer-title"
            onClick={(e) => e.stopPropagation()}
            className="absolute right-0 top-0 bottom-0 w-[86%] max-w-[340px] flex flex-col motion-reduce:transition-none transition-transform"
            style={{
              background: 'rgba(248,250,246,0.99)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              borderLeft: '1px solid rgba(214,224,207,0.55)',
              boxShadow: '-8px 0 32px rgba(27,48,34,0.18)',
              animation: 'slideInRight 0.22s ease-out',
            }}
          >
            {/* Drawer header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-arete-sage/40">
              <p
                id="mobile-nav-drawer-title"
                className="text-xs font-semibold uppercase tracking-wider text-arete-muted"
              >
                Navigation
              </p>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close navigation menu"
                className="p-2 min-w-[44px] min-h-[44px] rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-arete-blue/40"
                style={{ color: '#1B3022' }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            {/* Drawer body (scrollable) */}
            <nav className="flex-1 overflow-y-auto py-2" aria-label="Mobile navigation">
              <div className="divide-y divide-arete-sage/30">
                <MenuSection items={PRIMARY_NAV} onNavigate={handleNavigate} />
                <MenuSection label="More" items={SECONDARY_NAV} onNavigate={handleNavigate} />
                {(isClinician || isAdmin) && (
                  <MenuSection
                    label="Clinician"
                    items={[{ to: '/patients', label: 'Patients' }] as const}
                    onNavigate={handleNavigate}
                  />
                )}
                <MenuSection label="Account" items={PROFILE_NAV} onNavigate={handleNavigate} />
              </div>

              {/* Check-In status indicator inline in Overview */}
              {checkInDue && (
                <p className="px-4 py-2 text-[11px] text-arete-muted">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400 mr-2 align-middle" />
                  Check-in due
                </p>
              )}
            </nav>

            {/* Drawer footer — sign out */}
            <div className="border-t border-arete-sage/40 p-2">
              <button
                type="button"
                onClick={handleSignOut}
                className="w-full text-left px-4 min-h-[44px] rounded-lg text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-arete-blue/40"
                style={{ color: 'rgba(27,48,34,0.60)' }}
              >
                Sign Out
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* Inline keyframes — avoids adding a global animation file */}
      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @media (prefers-reduced-motion: reduce) {
          #mobile-nav-drawer,
          #mobile-nav-drawer + div {
            animation: none !important;
          }
        }
      `}</style>
    </>
  );
}
